import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';

const PendingSearcherModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    notes: '',
    searcherType: 'default'
  });
  const [sendReminderEmail, setSendReminderEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [createdUploadLink, setCreatedUploadLink] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email) {
      alert('Please fill in at least name and email.');
      return;
    }

    setSubmitting(true);
    try {
      const normalizedEmail = formData.email.toLowerCase().trim();
      const firstName = formData.name.trim().split(' ')[0] || '';
      const lastName = formData.name.trim().split(' ').slice(1).join(' ');
      const usesSecureUpload = ['franchise', 'startup'].includes(formData.searcherType);
      let sharedUploadPendingId = null;

      if (usesSecureUpload) {
        const loanRef = await addDoc(collection(db, 'loanApplications'), {
          firstName,
          lastName,
          email: normalizedEmail,
          phoneNumber: formData.phoneNumber || null,
          loanPurpose: 'Acquisition',
          loanStatus: 'new',
          searcherType: formData.searcherType,
          sharedUploadRoute: true,
          source: 'ben_portal',
          createdAt: serverTimestamp()
        });
        sharedUploadPendingId = loanRef.id;
      }

      // Create the pending searcher document
      const pendingSearcherData = {
        name: formData.name,
        email: normalizedEmail,
        phoneNumber: formData.phoneNumber || null,
        notes: formData.notes || null,
        createdAt: serverTimestamp(),
        lastReminderSentAt: null,
        reminderCount: 0,
        signedUp: false,
        metWith: true,
        searcherType: formData.searcherType,
        sharedUploadPendingId,
        suppressInitialEmail: !sendReminderEmail,
        source: 'ben_portal'
      };

      await addDoc(collection(db, 'pendingSearcherSignups'), pendingSearcherData);

      const uploadLink = sharedUploadPendingId
        ? `https://lended.ai/upload/${sharedUploadPendingId}`
        : '';

      // Reset form
      setFormData({
        name: '',
        email: '',
        phoneNumber: '',
        notes: '',
        searcherType: 'default'
      });
      setSendReminderEmail(true);

      if (onSuccess) {
        onSuccess();
      }
      if (uploadLink) {
        setCreatedUploadLink(uploadLink);
      } else {
        onClose();
        alert(`Pending searcher added${sendReminderEmail ? ' and signup email queued' : ''}.`);
      }
    } catch (error) {
      console.error('Error creating pending searcher:', error);
      alert('Error creating pending searcher. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  if (createdUploadLink) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-6 border w-full max-w-lg shadow-lg rounded-xl bg-white">
          <div className="flex justify-between items-start gap-4">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Secure upload link created</h3>
              <p className="mt-1 text-sm text-gray-500">
                The link is gated by the searcher's email address. {sendReminderEmail ? 'Their checklist email has been queued.' : 'No email was sent.'}
              </p>
            </div>
            <button onClick={() => { setCreatedUploadLink(''); onClose(); }} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="mt-5 rounded-lg border border-blue-200 bg-blue-50 p-4">
            <p className="break-all text-sm font-medium text-blue-900">{createdUploadLink}</p>
          </div>
          <div className="mt-5 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => navigator.clipboard.writeText(createdUploadLink)}
              className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
            >
              Copy link
            </button>
            <button
              type="button"
              onClick={() => { setCreatedUploadLink(''); onClose(); }}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Add Pending Searcher</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <p className="text-sm text-gray-500 mb-4">
          Add a searcher who hasn't signed up yet. They'll appear in the follow-up tab until they create an account.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Full name"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="email@example.com"
            />
          </div>

          {/* Phone Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="(555) 123-4567"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Next step *
            </label>
            <select
              name="searcherType"
              value={formData.searcherType}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="default">Create portal account</option>
              <option value="franchise">Franchise — secure document upload</option>
              <option value="startup">Startup — secure document upload</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Franchise and Startup create an email-gated upload link immediately.
            </p>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="How did they hear about us, etc."
            />
          </div>

          {/* Send Reminder Email Checkbox */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="sendReminderEmail"
              checked={sendReminderEmail}
              onChange={(e) => setSendReminderEmail(e.target.checked)}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor="sendReminderEmail" className="ml-2 block text-sm text-gray-700">
              {formData.searcherType === 'default' ? 'Send portal signup email' : 'Send checklist and secure upload link'}
            </label>
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed"
            >
              {submitting ? 'Adding...' : 'Add Searcher'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PendingSearcherModal;
